// @flow
import * as React from 'react';
import i18n from 'app/common/lib/i18n';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import type { BasicImage } from 'app/packages/pinterest-flow-types/image';
import type { Dispatch } from 'redux';
import type { ImagePositionType } from 'app/common/react/components/profile/ProfileBoardCard/commonTypes';
import { boardCoverImageUpdated } from 'app/common/redux/actions/board/update';
import BoardCoverImageCrop, {
  BoardCoverImageCropPlaceholder,
  type BoardCoverImageCropData,
} from 'app/common/react/components/BoardForm/BoardCoverImageCrop';
import withResource, { type Resource } from 'app/common/lib/react/withResource';
import { Box, Button } from 'gestalt';

type BoardFeedPinItem = {
  id: string,
  images: {
    '236x': BasicImage,
  },
  type: 'pin',
  url: string,
};

type BoardFeedStoryItem = { type: 'story' };

type BoardFeedItem = BoardFeedPinItem | BoardFeedStoryItem;

type OwnProps = {|
  board: {
    id: string,
    cover_pin: {
      pin_id: string,
    },
  },
  boardName: string,
  frameHeight?: number,
  frameWidth?: number,
  onCancel: () => void,
  onSave: (imageUrl: string, imagePosition: ImagePositionType) => void,
|};

type Props = {|
  ...OwnProps,
  boardFeedResource: Resource<Array<BoardFeedItem>>,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  dispatch: Dispatch<*>,
|};

type State = {|
  coverPinIndex: number,
  imgCropData: ?BoardCoverImageCropData,
  isSaving: boolean,
|};

function getPinsFromFeedItems(feedItems: Array<BoardFeedItem>): Array<BoardFeedPinItem> {
  return feedItems.reduce((arr, item) => {
    if (item.type === 'pin') {
      arr.push(item);
    }
    return arr;
  }, []);
}

function getCoverPinIndexFromItems({
  boardFeedResource: { data: items },
  board: {
    cover_pin: { pin_id: coverPinId },
  },
}: Props) {
  return Math.max(0, getPinsFromFeedItems(items || []).findIndex((pin) => pin.id === coverPinId));
}

const PREFETCH_LOOKAHEAD = 5;

export class BoardCoverEdit extends React.Component<Props, State> {
  state = {
    coverPinIndex: getCoverPinIndexFromItems(this.props),
    imgCropData: null,
    isSaving: false,
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const nextItems = nextProps.boardFeedResource.data;
    if (!this.props.boardFeedResource.data && nextItems) {
      this.setState({ coverPinIndex: getCoverPinIndexFromItems(nextProps) });
    }
  }

  componentDidUpdate(prevProps: Props) {
    const newItems = this.props.boardFeedResource.data;
    const oldItems = prevProps.boardFeedResource.data;
    // warming up images cache
    if (
      (newItems && oldItems !== newItems && newItems.length) ||
      (newItems && oldItems && newItems.length !== oldItems.length)
    ) {
      newItems.forEach((item) => {
        if (item.type === 'pin') {
          const img = new Image();
          img.src = item.url;
        }
      });
    }
  }

  get pins(): ?Array<BoardFeedPinItem> {
    const {
      boardFeedResource: { data },
    } = this.props;
    return data ? getPinsFromFeedItems(data) : undefined;
  }

  handleSave = () => {
    const { board, dispatch } = this.props;
    const { coverPinIndex, imgCropData } = this.state;
    const pin = (this.pins || [])[coverPinIndex];

    if (!pin) {
      return;
    }

    this.setState({ isSaving: true });

    const pinImg = pin.images['236x'];
    const pinImgUrl = pinImg.url;

    // Mix together data.options (the options that identify this board cover),
    // with the board cover data.
    // TODO: Once the board cover fields have been added to the API, make sure
    // that the names here match up.
    const resourceOptions = {
      board_id: board.id,
      url: pinImgUrl,
      pin_id: pin.id,
      pin_image_url: pinImgUrl,
      ...(imgCropData
        ? {
            crop_x: imgCropData.cropX,
            crop_y: imgCropData.cropY,
            height: imgCropData.height,
            width: imgCropData.width,
            scale: imgCropData.scale,
          }
        : {}),
    };

    const crop = {
      height: pinImg.height,
      width: pinImg.width,
      x: imgCropData ? imgCropData.cropX : 0,
      y: imgCropData ? imgCropData.cropY : 0,
    };

    ResourceFactory.create('BoardResource', resourceOptions)
      .callUpdate()
      .then(() => {
        this.setState({ isSaving: false });
        this.props.onSave(pinImgUrl, crop);
        dispatch(
          boardCoverImageUpdated(board.id, {
            custom_cover: true,
            scale: imgCropData ? imgCropData.scale : 1,
            pin_id: pin.id,
            crop,
            image_url: pinImgUrl,
          }),
        );
      })
      .catch(() => {
        this.setState({ isSaving: false });
      });
  };

  updateImgCrop = (imgCropData: BoardCoverImageCropData) =>
    this.setState({
      imgCropData,
    });

  handleShowNextPin = () => {
    const {
      boardFeedResource: { isAtEnd, isFetching, fetchMore },
    } = this.props;
    const pinCount = (this.pins || []).length;
    if (pinCount) {
      this.setState(
        ({ coverPinIndex }) => ({
          coverPinIndex: (coverPinIndex + 1) % pinCount,
        }),
        () => {
          // Prefetch the next page of images, so the user can keep flicking through seamlessly
          if (
            this.pins &&
            this.state.coverPinIndex + PREFETCH_LOOKAHEAD >= this.pins.length &&
            !isAtEnd &&
            !isFetching
          ) {
            fetchMore();
          }
        },
      );
    }
  };

  handleShowPrevPin = () => {
    const pinCount = (this.pins || []).length;
    if (pinCount) {
      this.setState(({ coverPinIndex }) => ({
        coverPinIndex: (coverPinIndex || pinCount) - 1,
      }));
    }
  };

  render() {
    const { boardName, frameHeight, frameWidth, onCancel } = this.props;
    const { coverPinIndex } = this.state;

    const pins = this.pins || [];
    const pin = pins[coverPinIndex];
    return (
      <React.Fragment>
        <Box position="relative" dangerouslySetInlineStyle={{ __style: { userSelect: 'none' } }}>
          {pin ? (
            <BoardCoverImageCrop
              boardName={boardName}
              frameHeight={frameHeight}
              frameWidth={frameWidth}
              isFirst={!coverPinIndex}
              isLast={coverPinIndex === pins.length - 1}
              pin={pin}
              showNextPin={this.handleShowNextPin}
              showPrevPin={this.handleShowPrevPin}
              updateImgCrop={this.updateImgCrop}
            />
          ) : (
            <BoardCoverImageCropPlaceholder />
          )}
        </Box>
        <Box display="flex" justifyContent="end">
          <Box padding={2}>
            <Button inline onClick={onCancel} text={i18n._('Cancel')} />
          </Box>
          <Box padding={2}>
            <Button
              disabled={!pin}
              inline
              onClick={this.handleSave}
              text={i18n._('Save Changes', 'Button for saving board cover edit')}
              color="red"
            />
          </Box>
        </Box>
      </React.Fragment>
    );
  }
}

export default withResource({
  name: 'BoardFeedResource',
  options: ({ board }: OwnProps) => ({
    board_id: board.id,
    field_set_key: 'images_only_item',
    filter_section_pins: false,
    page_size: 50,
  }),
  key: 'boardFeedResource',
})(BoardCoverEdit);
